import PropTypes from 'prop-types';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Stack,
    Typography,
    useMediaQuery
} from '@mui/material';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { loginUser } from 'store/slices/account';

// project imports
import AnimateButton from 'ui-component/extended/AnimateButton';
import AuthWrapper1 from 'views/pages/authentication/AuthWrapper1';
import AuthCardWrapper from 'views/pages/authentication/AuthCardWrapper';

import useAuth from 'hooks/useAuth';
import useConfig from 'hooks/useConfig';
import useScriptRef from 'hooks/useScriptRef';

// assets
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AuthLoginImage from 'assets/images/auth/img-a2-login.svg';


// ============================|| FIREBASE - LOGIN ||============================ //

const FirebaseLogin = ({ loginProp, ...others }) => {
    const theme = useTheme();
    const scriptedRef = useScriptRef();
    const { borderRadius } = useConfig();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const matchDownSM = useMediaQuery(theme.breakpoints.down('md'));

    const [checked, setChecked] = React.useState(true);

    const { firebaseEmailPasswordSignIn, firebaseGoogleSignIn, firebaseMicrosoftSignIn, firebaseFacebookSignIn } = useAuth();
    const googleHandler = async () => {
        try {
            await firebaseGoogleSignIn();
        } catch (err) {
            console.error(err);
        }
    };

    const microsoftHandler = async () => {
        try {
            await firebaseMicrosoftSignIn();
        } catch (err) {
            console.log(err);
        }
    };

    const facebookHandler = async () => {
        try {
            await firebaseFacebookSignIn();
        } catch (err) {
            console.log(err);
        }
    };

    const [showPassword, setShowPassword] = React.useState(false);
    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    return (
        <AuthWrapper1>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    minHeight: '100vh',
                    width: '100%',
                    margin: 0,
                    padding: 0
                }}
            >
                {/* Left side - Login Form */}
                <Box
                    sx={{
                        flex: { xs: '1 1 100%', md: '0 0 50%', lg: '0 0 41.67%' },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 3,
                        px: { xs: 2, sm: 3, md: 4 },
                        backgroundColor: 'transparent',
                        minHeight: { xs: 'auto', md: '100vh' }
                    }}
                >
                    <Box sx={{ width: '100%', maxWidth: 480 }}>
                        <AuthCardWrapper>
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Typography 
                                variant="h3" 
                                sx={{ 
                                    fontWeight: 700,
                                    color: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.primary.main,
                                    mb: 1
                                }}
                            >
                                Chào mừng trở lại
                            </Typography>
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    color: theme.palette.text.secondary,
                                    mb: 0.5
                                }}
                            >
                                Đăng nhập vào tài khoản của bạn
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Formik
                initialValues={{
                    email: '',
                    password: '',
                    submit: null
                }}
                validationSchema={Yup.object().shape({
                    email: Yup.string().email('Email không hợp lệ').max(255).required('Vui lòng điền Email'),
                    password: Yup.string().max(255).required('Vui lòng điền mật khẩu')
                })}
                onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                    try {
                        // Đăng nhập qua RTK Query (login/access-token)
                        const params = new URLSearchParams();
                        params.append('username', values.email);
                        params.append('password', values.password);
                        const apiSlice = (await import('store/slices/apiSlice')).default;
                        const res = await dispatch(apiSlice.endpoints.loginAccessToken.initiate(params)).unwrap();
                        if (res?.access_token) {
                            const { authManager } = await import('utils/authManager');
                            authManager.setToken(res.access_token, 'auth_token');
                            try {
                                const userRes = await dispatch(apiSlice.endpoints.loginTestToken.initiate(undefined)).unwrap();
                                if (userRes?.user_token) localStorage.setItem('user_token', userRes.user_token);
                                if (userRes?.email) localStorage.setItem('user_email', userRes.email);
                                dispatch(loginUser({
                                    user: { ...userRes, is_profile: true }
                                }));
                            } catch (userErr) {
                                dispatch(loginUser({
                                    user: { email: values.email, is_profile: true }
                                }));
                            }
                            axios.defaults.headers.common.Authorization = `Bearer ${res.access_token}`;
                            
                            if (scriptedRef.current) {
                                setStatus({ success: true });
                                setSubmitting(false);
                                
                                // Redirect đến dashboard sau khi login thành công
                                setTimeout(() => {
                                    navigate('/datahub/dashboard', { replace: true });
                                }, 100);
                            }
                        } else {
                            throw new Error('Không nhận được access_token từ backend');
                        }
                    } catch (err) {
                        if (scriptedRef.current) {
                            setStatus({ success: false });
                            setErrors({ submit: err?.data?.detail || err?.response?.data?.detail || err.message || 'Đăng nhập thất bại' });
                            setSubmitting(false);
                        }
                    }
                }}
            >
                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                    <form noValidate onSubmit={handleSubmit} {...others}>
                        <Box>
                            <FormControl 
                                fullWidth 
                                error={Boolean(touched.email && errors.email)} 
                                sx={{ 
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        },
                                        '&.Mui-focused': {
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                        }
                                    }
                                }}
                            >
                                <InputLabel htmlFor="outlined-adornment-email-login">Email</InputLabel>
                                <OutlinedInput
                                    id="outlined-adornment-email-login"
                                    type="email"
                                    value={values.email}
                                    name="email"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    label="Email"
                                    placeholder="Nhập email của bạn"
                                />
                                {touched.email && errors.email && (
                                    <FormHelperText error id="standard-weight-helper-text-email-login" sx={{ mt: 0.5 }}>
                                        {errors.email}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Box>

                        <Box>
                            <FormControl
                                fullWidth
                                error={Boolean(touched.password && errors.password)}
                                sx={{
                                    mb: 1,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        },
                                        '&.Mui-focused': {
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                        }
                                    }
                                }}
                            >
                                <InputLabel htmlFor="outlined-adornment-password-login">Mật khẩu</InputLabel>
                                <OutlinedInput
                                    id="outlined-adornment-password-login"
                                    type={showPassword ? 'text' : 'password'}
                                    value={values.password}
                                    name="password"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    placeholder="Nhập mật khẩu của bạn"
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                                size="large"
                                                sx={{ color: theme.palette.text.secondary }}
                                            >
                                                {showPassword ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    label="Mật khẩu"
                                />
                                {touched.password && errors.password && (
                                    <FormHelperText error id="standard-weight-helper-text-password-login" sx={{ mt: 0.5 }}>
                                        {errors.password}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                        <Box>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={checked}
                                            onChange={(event) => setChecked(event.target.checked)}
                                            name="checked"
                                            color="primary"
                                            sx={{
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: 20
                                                }
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                            Ghi nhớ đăng nhập
                                        </Typography>
                                    }
                                />
                                <Typography
                                    variant="body2"
                                    component={Link}
                                    to={'/forgot'}
                                    sx={{
                                        color: theme.palette.primary.main,
                                        textDecoration: 'none',
                                        fontWeight: 500,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                            color: theme.palette.primary.dark
                                        }
                                    }}
                                >
                                    Quên mật khẩu?
                                </Typography>
                            </Stack>
                        </Box>

                        {errors.submit && (
                            <Box>
                                <Box 
                                    sx={{ 
                                        p: 2, 
                                        borderRadius: 2,
                                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.1)' : 'rgba(211, 47, 47, 0.05)',
                                        border: `1px solid ${theme.palette.error.main}`
                                    }}
                                >
                                    <FormHelperText error sx={{ m: 0, fontSize: '0.875rem' }}>
                                        {errors.submit}
                                    </FormHelperText>
                                </Box>
                            </Box>
                        )}

                        <Box>
                            <AnimateButton>
                                <Button
                                    disableElevation
                                    disabled={isSubmitting}
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                    sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        color: '#ffffff',
                                        py: 1.75,
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        boxShadow: '0 4px 14px 0 rgba(0, 118, 255, 0.39)',
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.dark,
                                            boxShadow: '0 6px 20px rgba(0, 118, 255, 0.5)',
                                            transform: 'translateY(-2px)'
                                        },
                                        '&:active': {
                                            transform: 'translateY(0px)'
                                        },
                                        '&:disabled': {
                                            backgroundColor: theme.palette.action.disabledBackground,
                                            color: theme.palette.action.disabled
                                        },
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}
                                >
                                    {isSubmitting ? 'Đang đăng nhập...' : 'Đăng nhập'}
                                </Button>
                            </AnimateButton>
                        </Box>

                        <Box>
                            <Box sx={{ textAlign: 'center', pt: 2 }}>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: theme.palette.text.secondary,
                                        display: 'inline',
                                        mr: 0.5
                                    }}
                                >
                                    Chưa có tài khoản?
                                </Typography>
                                <Typography
                                    component={Link}
                                    to="/register"
                                    variant="body2"
                                    sx={{
                                        color: theme.palette.primary.main,
                                        textDecoration: 'none',
                                        fontWeight: 600,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                            color: theme.palette.primary.dark
                                        }
                                    }}
                                >
                                    Đăng ký ngay
                                </Typography>
                            </Box>
                        </Box>
                        </form>
                    )}
                </Formik>
                        </Box>
                        </AuthCardWrapper>
                    </Box>
                </Box>

                {/* Right side - Background Image */}
                <Box
                    sx={{
                        flex: { xs: '0 0 0', md: '0 0 50%', lg: '0 0 58.33%' },
                        display: { xs: 'none', md: 'flex' },
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                        minHeight: '100vh',
                        backgroundColor: theme.palette.mode === 'dark' 
                            ? theme.palette.background.default
                            : '#f5f7fa',
                        overflow: 'hidden',
                        padding: { md: 3, lg: 4 }
                    }}
                >
                    <Box
                        component="img"
                        src={AuthLoginImage}
                        alt="Login Illustration"
                        sx={{
                            width: '100%',
                            height: 'auto',
                            maxWidth: '700px',
                            maxHeight: '85vh',
                            objectFit: 'contain',
                            objectPosition: 'center',
                            opacity: theme.palette.mode === 'dark' ? 0.9 : 1,
                            filter: theme.palette.mode === 'dark' ? 'brightness(0.9)' : 'none',
                            transition: 'all 0.3s ease',
                            zIndex: 1,
                            position: 'relative'
                        }}
                    />
                </Box>
            </Box>
        </AuthWrapper1>
    );
};

FirebaseLogin.propTypes = {
    loginProp: PropTypes.number
};

export default FirebaseLogin;
